import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faTriangleExclamation, faWandMagicSparkles, faCalendarDay } from '@fortawesome/free-solid-svg-icons';
import './TeacherPay.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const tenure = (months) => {
  const y = Math.floor(months / 12);
  const m = months % 12;
  const parts = [];
  if (y) parts.push(`${y} year${y > 1 ? 's' : ''}`);
  if (m || !y) parts.push(`${m} month${m === 1 ? '' : 's'}`);
  return parts.join(' ');
};
const day = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

/* Admin › teacher profile › Pay & stats (admins only).
   Her own Standard / Mastery rates, how long she has been at RoboMind with the suggested tier,
   and everything she has done since she started. */
const TeacherPay = ({ teacherId }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const toForm = (d) => ({
    rateStandard: d.rates.set ? String(d.rates.standard) : '',
    rateMastery: d.rates.set ? String(d.rates.mastery) : '',
    startDate: d.startFrom === 'set' ? d.startDate : '',
  });

  useEffect(() => {
    if (!teacherId) return undefined;
    let alive = true;
    axios
      .get(`${API}/api/admin/teacherProfile/${teacherId}/pay`, auth())
      .then((res) => {
        if (!alive) return;
        setData(res.data);
        setForm(toForm(res.data));
      })
      .catch((err) => alive && setError(err.response?.data?.error || 'We couldn’t load the pay and stats.'));
    return () => {
      alive = false;
    };
  }, [teacherId]);

  const save = async (next = form) => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await axios.put(`${API}/api/admin/teacherProfile/${teacherId}/pay`, next, auth());
      setData(res.data);
      setForm(toForm(res.data));
      setMsg({ ok: true, text: 'Saved. Classes you approve from now on use these rates.' });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Couldn’t save.' });
    } finally {
      setSaving(false);
    }
  };

  if (error) return <p className="rmtpy-msg is-bad">{error}</p>;
  if (!data || !form) return <div className="rmtpy-skel" aria-busy="true" />;

  const { stats, tier } = data;
  const changed = JSON.stringify(form) !== JSON.stringify(toForm(data));
  const useTier = () => {
    const next = { ...form, rateStandard: String(tier.standard), rateMastery: String(tier.mastery) };
    setForm(next);
  };

  return (
    <div className="rmtpy">
      {/* ---------- Time at RoboMind ---------- */}
      <section className="rmtpy-card rmtpy-tenure">
        <div>
          <span className="rmtpy-label">At RoboMind</span>
          <strong>{data.startDate ? tenure(data.months) : 'No classes yet'}</strong>
          <p>
            Since {day(data.startDate)}
            {data.startFrom === 'first class' && ' (her first class or demo)'}
            {data.startFrom === 'set' && ' (date you set)'}
          </p>
        </div>
        <label className="rmtpy-field">
          <span>
            <FontAwesomeIcon icon={faCalendarDay} /> Start date (empty = first class)
          </span>
          <input type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} disabled={saving} />
        </label>
      </section>

      {/* ---------- Her rates ---------- */}
      <section className="rmtpy-card">
        <span className="rmtpy-label">Her rates (per class)</span>
        <div className="rmtpy-rates">
          <label className="rmtpy-field">
            <span>Standard</span>
            <div className="rmtpy-money">
              <b>$</b>
              <input
                type="number"
                min="0"
                step="0.25"
                inputMode="decimal"
                placeholder={String(data.defaults.standard)}
                value={form.rateStandard}
                onChange={(e) => setForm((f) => ({ ...f, rateStandard: e.target.value }))}
                disabled={saving}
              />
            </div>
          </label>
          <label className="rmtpy-field">
            <span>Mastery</span>
            <div className="rmtpy-money">
              <b>$</b>
              <input
                type="number"
                min="0"
                step="0.25"
                inputMode="decimal"
                placeholder={String(data.defaults.mastery)}
                value={form.rateMastery}
                onChange={(e) => setForm((f) => ({ ...f, rateMastery: e.target.value }))}
                disabled={saving}
              />
            </div>
          </label>
          <button type="button" className="rmtpy-save" onClick={() => save()} disabled={!changed || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        <p className="rmtpy-note">
          Paid now: <b>{money(data.rates.standard)}</b> Standard · <b>{money(data.rates.mastery)}</b> Mastery
          {!data.rates.set && ' (default rates)'}. Empty = default. Classes already approved keep the rate they were paid.
        </p>
        {msg && (
          <p className={`rmtpy-msg ${msg.ok ? '' : 'is-bad'}`} role="status">
            <FontAwesomeIcon icon={msg.ok ? faCircleCheck : faTriangleExclamation} /> {msg.text}
          </p>
        )}

        <div className="rmtpy-tier">
          <div>
            <span className="rmtpy-label">Suggested for {tier.label.toLowerCase()} · only you see this</span>
            <p>
              {money(tier.standard)} Standard · {money(tier.mastery)} Mastery
            </p>
          </div>
          <button type="button" className="rmtpy-ghost" onClick={useTier} disabled={saving}>
            <FontAwesomeIcon icon={faWandMagicSparkles} /> Use these
          </button>
        </div>
        <ul className="rmtpy-tiers">
          {data.tiers.map((t) => (
            <li key={t.fromMonths} className={t.fromMonths === tier.fromMonths ? 'is-on' : ''}>
              <span>{t.label}</span>
              <b>
                {money(t.standard)} / {money(t.mastery)}
              </b>
            </li>
          ))}
        </ul>
      </section>

      {/* ---------- Since she started ---------- */}
      <section className="rmtpy-card">
        <span className="rmtpy-label">Since she started</span>
        <ul className="rmtpy-stats">
          <li>
            <b>{stats.total}</b>
            <span>classes</span>
          </li>
          <li>
            <b>{stats.standard}</b>
            <span>Standard</span>
          </li>
          <li>
            <b>{stats.mastery}</b>
            <span>Mastery</span>
          </li>
          <li>
            <b>{stats.half + stats.noPay}</b>
            <span>half / no pay</span>
          </li>
          <li>
            <b>{stats.students}</b>
            <span>students taught</span>
          </li>
          <li>
            <b>{stats.demos}</b>
            <span>demos</span>
          </li>
          <li>
            <b>{stats.enrolled}</b>
            <span>
              enrolled from her demos{stats.conversion != null ? ` (${stats.conversion}%)` : ''}
            </span>
          </li>
          <li>
            <b>{money(stats.earned)}</b>
            <span>earned from classes</span>
          </li>
        </ul>
        {stats.noCourse > 0 && <p className="rmtpy-note">{stats.noCourse} classes have no course recorded (counted in the total only).</p>}
      </section>
    </div>
  );
};

export default TeacherPay;
